import React, { useState, useEffect } from "react";
import {Building, BookOpen, Laptop, Heart, CheckCircle, Calendar, Upload, X, Image as ImageIcon, Trash2} from 'lucide-react';
import { lumi } from '../lib/lumi';
import { EditableText } from '../components/EditableText';

function Events() {
  const [events, setEvents] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadEvents();
    checkUserRole();
  }, []);

  async function checkUserRole() {
    const user = lumi.auth.user;
    setIsAdmin(user?.userRole === "ADMIN");
  }

  async function loadEvents() {
    try {
      const result = await lumi.entities.events.list({
        sort: { date: 1 },
        limit: 100
      });
      setEvents(result?.list || []);
    } catch (error) {
      console.error("Failed to load events:", error);
    }
  }

  async function handleCreateEvent(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const title = formData.get("title") as string;
    const description = formData.get("description") as string;
    const date = formData.get("date") as string;
    const status = formData.get("status") as string;
    const impact = formData.get("impact") as string;

    if (!title || !description || !date || !status) {
      alert("Please fill all required fields");
      return;
    }

    setUploading(true);
    try {
      await lumi.entities.events.create({
        title,
        description,
        date,
        status,
        impact: impact || "",
        mediaUrls: [],
        uploadedBy: lumi.auth.user?.userName || "Admin",
        createdAt: new Date()
      });

      alert("Event created successfully!");
      setShowEventModal(false);
      loadEvents();
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error("Failed to create event:", error);
      alert("Failed to create event. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handleUploadMedia(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const files = formData.getAll("files") as File[];
    const eventId = formData.get("eventId") as string;

    if (!files.length || !eventId) {
      alert("Please select files and event");
      return;
    }

    setUploading(true);
    try {
      const uploadResults = await lumi.tools.file.upload(files);
      const mediaUrls = uploadResults.filter((r) => r.fileUrl).map((r) => r.fileUrl as string);

      const event = events.find((e) => e._id === eventId);
      if (event) {
        const updatedMediaUrls = [...(event.mediaUrls || []), ...mediaUrls];
        await lumi.entities.events.update(eventId, {
          mediaUrls: updatedMediaUrls
        });
      }

      alert("Media uploaded successfully!");
      setShowUploadModal(false);
      loadEvents();
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="min-h-screen py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4 text-[#DC143C]">MOBA91 Events</h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Connecting alumni through memorable gatherings and meaningful activities
          </p>
          
          {isAdmin &&
          <div className="flex justify-center gap-4 mt-6">
              <button
              onClick={() => setShowEventModal(true)}
              className="px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2">
                <Calendar size={20} />
                Create Event
              </button>
              <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2">
                <Upload size={20} />
                Upload Media
              </button>
            </div>
          }
        </div>

        {/* Events Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {events.map((event) =>
          <EventCard key={event._id} event={event} />
          )}
        </div>

        {events.length === 0 &&
        <div className="mt-16 text-center bg-white border-2 border-gray-200 rounded-2xl p-12 shadow-lg">
            <Calendar size={48} className="text-[#DC143C] mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3 text-black">No Events Yet</h3>
            <p className="text-gray-600 max-w-xl mx-auto">
              {isAdmin ? "Click 'Create Event' to add events" : "Check back soon for upcoming events"}
            </p>
          </div>
        }

        {/* Impact Summary */}
        <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-black">Our Impact</h2>
            <p className="text-gray-600">Making a difference at Mfantsipim School</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            <ImpactStat value="GH₵ 500K+" label="Total Contributions" />
            <ImpactStat value={`${events.length}+`} label="Total Events" />
            <ImpactStat value="1000+" label="Students Impacted" />
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#DC143C] to-red-600 rounded-2xl p-12 text-[#991B1B] bg-[#991B1B]">
          <h2 className="text-3xl font-bold mb-4 text-[#FFFFFF]">Join Our Mission</h2>
          <p className="mb-8 max-w-2xl mx-auto text-[#FFFFFF] text-xl">Your contributions help us continue making a positive impact at Mfantsipim School. Every donation counts.

          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-black hover:bg-[#1A1A1A] rounded-lg font-semibold transition-all transform hover:scale-105 text-2xl">Support Our Events

          </a>
        </div>
      </div>

      {/* Create Event Modal */}
      {showEventModal &&
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-[#DC143C] rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-black">Create Event</h2>
              <button onClick={() => setShowEventModal(false)} className="text-gray-600 hover:text-black">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateEvent} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-black">Title *</label>
                <input
                type="text"
                name="title"
                required
                className="w-full px-4 py-3 bg-white text-black border-2 border-gray-300 rounded-lg focus:border-[#DC143C] focus:outline-none"
                placeholder="Event title" />

              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-black">Description *</label>
                <textarea
                name="description"
                required
                rows={4}
                className="w-full px-4 py-3 bg-white text-black border-2 border-gray-300 rounded-lg focus:border-[#DC143C] focus:outline-none"
                placeholder="Event description" />

              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-black">Date * (Click to open calendar)</label>
                <input
                type="date"
                name="date"
                required
                className="w-full px-4 py-3 bg-white text-black border-2 border-gray-300 rounded-lg focus:border-[#DC143C] focus:outline-none cursor-pointer"
                placeholder="Select date" />

              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-black">Status *</label>
                <select
                name="status"
                required
                className="w-full px-4 py-3 bg-white text-black border-2 border-gray-300 rounded-lg focus:border-[#DC143C] focus:outline-none">
                  <option value="">Select status</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Annual">Annual</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-black">Impact</label>
                <input
                type="text"
                name="impact"
                className="w-full px-4 py-3 bg-white text-black border-2 border-gray-300 rounded-lg focus:border-[#DC143C] focus:outline-none"
                placeholder="Expected impact or achievement" />

              </div>

              <div className="flex gap-4">
                <button
                type="button"
                onClick={() => setShowEventModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-black rounded-lg font-semibold transition-all">
                  Cancel
                </button>
                <button
                type="submit"
                disabled={uploading}
                className="flex-1 px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50">
                  {uploading ? "Creating..." : "Create Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      }

      {/* Upload Media Modal */}
      {showUploadModal &&
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-[#DC143C] rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-black">Upload Event Media</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-600 hover:text-black">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUploadMedia} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-black">Select Event *</label>
                <select
                name="eventId"
                required
                className="w-full px-4 py-3 bg-white text-black border-2 border-gray-300 rounded-lg focus:border-[#DC143C] focus:outline-none">
                  <option value="">Choose an event</option>
                  {events.map((event) =>
                <option key={event._id} value={event._id}>{event.title}</option>
                )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-black">Files (Photos/Videos) *</label>
                <input
                type="file"
                name="files"
                multiple
                required
                accept="image/*,video/*"
                className="w-full px-4 py-3 bg-white text-black border-2 border-gray-300 rounded-lg focus:border-[#DC143C] focus:outline-none" />

                <p className="text-gray-600 text-sm mt-2">Supports: JPG, PNG, MP4, MOV</p>
              </div>

              <div className="flex gap-4">
                <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-black rounded-lg font-semibold transition-all">
                  Cancel
                </button>
                <button
                type="submit"
                disabled={uploading}
                className="flex-1 px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50">
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    </div>);

}

function EventCard({ event }: any) {
  const isCompleted = event.status === "Completed";
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editedDate, setEditedDate] = useState(event.date);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const user = lumi.auth.user;
    setIsAdmin(user?.userRole === "ADMIN");
  }, []);

  const hasMedia = event.mediaUrls && event.mediaUrls.length > 0;
  const currentMedia = hasMedia ? event.mediaUrls[currentMediaIndex] : null;
  const isVideo = currentMedia?.match(/\.(mp4|mov|avi|mkv|webm)$/i);

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleVideoEnded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.pause();
    }
  };

  const handleDeleteMedia = async (mediaUrl: string) => {
    if (!confirm("Are you sure you want to delete this media?")) return;

    try {
      const updatedMediaUrls = event.mediaUrls.filter((url: string) => url !== mediaUrl);
      await lumi.entities.events.update(event._id, {
        mediaUrls: updatedMediaUrls
      });
      await lumi.tools.file.delete([mediaUrl]);
      alert("Media deleted successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Failed to delete media:", error);
      alert("Failed to delete media. Please try again.");
    }
  };

  const handleDateSave = async () => {
    if (!editedDate) {
      alert("Please select a date");
      return;
    }

    try {
      await lumi.entities.events.update(event._id, {
        date: editedDate
      });
      alert("Date updated successfully!");
      setIsEditingDate(false);
      window.location.reload();
    } catch (error) {
      console.error("Failed to update date:", error);
      alert("Failed to update date. Please try again.");
    }
  };

  return (
    <>
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#DC143C] transition-all group shadow-lg">
        <div className="flex flex-col md:flex-row">
          {/* Left Side - Event Details */}
          <div className="flex-1 p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-all">
                <Calendar className="text-[#DC143C]" size={24} />
              </div>
              <div
                className={`px-4 py-1 rounded-full text-sm font-semibold ${
                isCompleted ?
                "bg-green-500/10 text-green-400 border border-green-500/30" :
                "bg-blue-500/10 text-blue-400 border border-blue-500/30"}`
                }>
                {event.status}
              </div>
            </div>

            <EditableText
              pageId="events"
              elementId={`event-title-${event._id}`}
              defaultContent={event.title}
              className="text-2xl font-bold mb-3 group-hover:text-[#DC143C] transition-colors text-[#FFFFFF]"
              as="h3"
            />

            <EditableText
              pageId="events"
              elementId={`event-description-${event._id}`}
              defaultContent={event.description}
              className="mb-4 leading-relaxed text-[#991B1B] block"
              as="p"
              multiline={true}
            />

            <div className="text-sm text-gray-600 mb-4 flex items-center gap-2">
              <Calendar size={16} className="inline" />
              {isEditingDate && isAdmin ? (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={editedDate}
                    onChange={(e) => setEditedDate(e.target.value)}
                    className="px-3 py-1 bg-white text-black border-2 border-[#DC143C] rounded-lg focus:outline-none"
                  />
                  <button
                    onClick={handleDateSave}
                    className="px-3 py-1 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg text-xs font-semibold"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingDate(false);
                      setEditedDate(event.date);
                    }}
                    className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-black rounded-lg text-xs font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <span>{event.date}</span>
                  {isAdmin && (
                    <button
                      onClick={() => setIsEditingDate(true)}
                      className="ml-2 text-[#DC143C] hover:text-red-600 text-xs underline"
                    >
                      Edit Date
                    </button>
                  )}
                </>
              )}
            </div>

            {event.impact &&
            <div className="pt-4 border-t border-gray-200">
                <div className="flex items-start space-x-2 text-sm text-gray-700">
                  <CheckCircle size={20} className="text-[#DC143C] flex-shrink-0 mt-0.5" />
                  <EditableText
                    pageId="events"
                    elementId={`event-impact-${event._id}`}
                    defaultContent={event.impact}
                    className="text-2xl"
                    as="span"
                  />
                </div>
              </div>
            }

            {hasMedia && event.mediaUrls.length > 1 &&
            <button
              onClick={() => setShowMediaModal(true)}
              className="mt-4 w-full px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-semibold text-black transition-all flex items-center justify-center gap-2">
                <ImageIcon size={16} />
                View All Media ({event.mediaUrls.length})
              </button>
            }
          </div>

          {/* Right Side - Media Display */}
          {hasMedia &&
          <div className="md:w-1/2 relative bg-gray-100 flex items-center justify-center p-4">
              {isAdmin && (
                <button
                  onClick={() => handleDeleteMedia(currentMedia)}
                  className="absolute top-6 right-6 z-10 p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-all"
                  title="Delete this media"
                >
                  <Trash2 size={20} className="text-white" />
                </button>
              )}
              <div className="w-full h-full flex items-center justify-center">
                {isVideo ?
              <video
                ref={videoRef}
                src={currentMedia}
                className="w-full h-auto max-h-[400px] rounded-lg cursor-pointer object-cover"
                onClick={handleVideoClick}
                onEnded={handleVideoEnded}
                playsInline /> :


              <img
                src={currentMedia}
                alt={`${event.title} media`}
                className="w-full h-auto max-h-[400px] rounded-lg object-cover" />

              }
              </div>

              {/* Media Navigation */}
              {event.mediaUrls.length > 1 &&
            <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
                  {event.mediaUrls.map((_: any, index: number) =>
              <button
                key={index}
                onClick={() => setCurrentMediaIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                index === currentMediaIndex ?
                'bg-[#DC143C] w-6' :
                'bg-gray-500 hover:bg-gray-400'}`
                } />

              )}
                </div>
            }
            </div>
          }
        </div>
      </div>

      {/* Media Modal */}
      {showMediaModal &&
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setShowMediaModal(false)}>
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{event.title} - Media</h2>
              <button onClick={() => setShowMediaModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {event.mediaUrls.map((url: string, index: number) =>
            <div key={index} className="relative rounded-lg overflow-hidden bg-[#0A0A0A] group">
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteMedia(url)}
                      className="absolute top-2 right-2 z-10 p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      title="Delete this media"
                    >
                      <Trash2 size={16} className="text-white" />
                    </button>
                  )}
                  {url.match(/\.(mp4|mov|avi|mkv|webm)$/i) ?
              <video src={url} controls className="w-full h-auto" /> :

              <img src={url} alt={`${event.title} ${index + 1}`} className="w-full h-auto" />
              }
                </div>
            )}
            </div>
          </div>
        </div>
      }
    </>);

}

function ImpactStat({ value, label }: {value: string;label: string;}) {
  return (
    <div>
      <div className="text-4xl font-bold mb-2 bg-[#000000] text-[#991B1B]">{value}</div>
      <div className="text-gray-400">{label}</div>
    </div>);

}

export default Events;